import type { ServicePageContent } from '../types/servicePage'

export const newServicePages: ServicePageContent[] = [
  {
    slug: 'drywall-installation-las-vegas',
    name: 'Drywall Installation',
    title: 'Drywall installation',
    serviceId: 'drywall-install',
    intro:
      'New drywall hung, taped and finished for Las Vegas homes — new walls, garage finishing and full tear-outs, ready for paint.',
    coverageIntro: 'We handle the whole job, from bare studs to a finished wall.',
    coverage: [
      { title: 'New walls', text: 'Drywall for new rooms, partitions and additions.' },
      { title: 'Garage finishing', text: 'Unfinished garage walls hung, taped and finished.' },
      { title: 'Full tear-outs', text: 'Old or damaged drywall removed and replaced.' },
      { title: 'Hanging', text: 'Sheets cut and fastened to fit your walls and ceilings.' },
      { title: 'Taping & finishing', text: 'Seams taped, mudded and sanded smooth.' },
      { title: 'Texture to match', text: 'A finish that matches the rest of your home.' },
    ],
    sections: [
      {
        title: 'What drywall installation involves',
        body: [
          'We cut and hang new drywall, tape and mud the seams, sand everything smooth and apply the texture you want. When we’re done, the wall is ready for paint.',
        ],
      },
      {
        title: 'Garages and tear-outs',
        body: [
          'Finishing a garage makes it cleaner and more usable. For tear-outs, we remove the old drywall and install new panels so the room looks like it was never touched.',
        ],
      },
      {
        title: 'Need paint too?',
        body: ['We also offer painting, so your new walls can go from bare drywall to finished color in one project.'],
      },
    ],
    related: [
      'drywall-services-las-vegas',
      'texture-matching-las-vegas',
      'painting-services-las-vegas',
      'water-damage-repair-las-vegas',
    ],
  },
  {
    slug: 'flooring-installation-las-vegas',
    name: 'Flooring Installation',
    title: 'Flooring installation',
    serviceId: 'flooring',
    intro:
      'Removal of old flooring and installation of LVP, laminate and tile in Las Vegas homes — with baseboards included.',
    coverageIntro: 'We take care of the old floor and the new one, so you only deal with one crew.',
    coverage: [
      { title: 'Old flooring removal', text: 'Existing carpet, laminate or tile taken out.' },
      { title: 'Luxury vinyl plank (LVP)', text: 'A durable, wood-look floor for busy homes.' },
      { title: 'Laminate', text: 'An affordable option in many styles.' },
      { title: 'Tile', text: 'Tile floors for kitchens, baths and entryways.' },
      { title: 'Baseboards', text: 'Baseboards installed to finish the room.' },
    ],
    sections: [
      {
        title: 'From removal to finished floor',
        body: [
          'We remove your existing flooring, prepare the space and install your new floor. Then we add baseboards so the room looks finished.',
        ],
      },
      {
        title: 'Pairs well with paint',
        body: [
          'Many customers update floors and walls at the same time. We can paint the room too, so everything is done in one project.',
        ],
      },
    ],
    related: [
      'doors-trim-baseboards-las-vegas',
      'tile-backsplash-las-vegas',
      'painting-services-las-vegas',
      'carpentry-services-las-vegas',
    ],
  },
  {
    slug: 'water-restoration-las-vegas',
    name: 'Water Restoration',
    title: 'Water restoration',
    serviceId: 'water-restoration',
    intro:
      'Water extraction, drying and rebuild for Las Vegas homes after leaks, floods and burst pipes. Call us as soon as you find water where it shouldn’t be.',
    coverageIntro: 'We handle the full job — getting the water out, drying the space and putting it back together.',
    coverage: [
      { title: 'Water extraction', text: 'Standing water removed from floors and affected areas.' },
      { title: 'Drying', text: 'Walls, floors and cavities dried out before rebuilding.' },
      { title: 'Damaged material removal', text: 'Wet drywall, flooring and baseboards taken out where needed.' },
      { title: 'Rebuild', text: 'New drywall, flooring and trim installed and finished.' },
      { title: 'Burst pipes & leaks', text: 'Cleanup and rebuild after plumbing failures.' },
      { title: 'Flooding', text: 'Restoration after water spreads through a room or home.' },
    ],
    sections: [
      {
        title: 'Why speed matters',
        body: [
          'The longer water sits, the more it soaks into drywall, flooring and framing. Getting it out and drying the area quickly helps limit how much needs to be replaced.',
        ],
      },
      {
        title: 'One team from cleanup to finish',
        body: [
          'Because we also do drywall, painting and flooring, the same team that dries out your home can rebuild it. You don’t need to find a second contractor to finish the job.',
        ],
      },
    ],
    related: [
      'water-damage-repair-las-vegas',
      'mold-remediation-las-vegas',
      'drywall-services-las-vegas',
      'flooring-installation-las-vegas',
    ],
  },
  {
    slug: 'mold-remediation-las-vegas',
    name: 'Mold Remediation',
    title: 'Mold remediation',
    serviceId: 'mold-remediation',
    intro:
      'Mold found, safely removed and the damaged material replaced in Las Vegas homes — so you can get back to a clean, healthy space.',
    coverageIntro: 'We find the mold, remove it and rebuild what it damaged.',
    coverage: [
      { title: 'Finding the mold', text: 'We locate where mold is growing and how far it has spread.' },
      { title: 'Safe removal', text: 'Affected areas removed with care to limit spreading spores.' },
      { title: 'Replacing damaged material', text: 'Drywall, insulation, trim and other affected materials replaced.' },
      { title: 'Finishing', text: 'Walls patched, textured and painted to match.' },
    ],
    sections: [
      {
        title: 'Where mold comes from',
        body: [
          'Mold usually grows where moisture has been sitting — behind walls after a leak, under sinks or around windows. Finding the source of the moisture is part of fixing the problem.',
        ],
      },
      {
        title: 'Removal and rebuild in one',
        body: [
          'After the mold and damaged material are removed, we replace the drywall and finish the surface so the room looks the way it should.',
        ],
      },
    ],
    related: [
      'water-damage-repair-las-vegas',
      'water-restoration-las-vegas',
      'drywall-services-las-vegas',
      'painting-services-las-vegas',
    ],
  },
  {
    slug: 'water-damage-repair-las-vegas',
    name: 'Water Damage Repair',
    title: 'Water damage repair',
    serviceId: 'water-damage',
    intro:
      'Leak-damaged walls and ceilings cut out, dried, rebuilt and matched to the rest of your Las Vegas home.',
    coverageIntro: 'We repair the damage a leak leaves behind, so you can’t tell it was ever there.',
    coverage: [
      { title: 'Stained ceilings & walls', text: 'Water stains removed or the damaged section replaced.' },
      { title: 'Cut-out & replace', text: 'Soft or sagging drywall cut out and new drywall installed.' },
      { title: 'Drying', text: 'The area dried before it’s closed back up.' },
      { title: 'Texture matching', text: 'New drywall textured to match the surrounding surface.' },
      { title: 'Paint', text: 'Repaired areas primed and painted to blend in.' },
    ],
    sections: [
      {
        title: 'How the repair works',
        body: [
          'We cut out the damaged section, make sure the area is dry, install new drywall, tape and mud the seams, match your texture and paint. The goal is a repair you can’t find.',
        ],
      },
      {
        title: 'Fix the leak first',
        body: [
          'Before we rebuild, the source of the leak needs to be fixed so the damage doesn’t come back. We’ll talk through this with you during the estimate.',
        ],
      },
    ],
    related: [
      'water-restoration-las-vegas',
      'mold-remediation-las-vegas',
      'texture-matching-las-vegas',
      'drywall-services-las-vegas',
    ],
  },
  {
    slug: 'popcorn-ceiling-removal-las-vegas',
    name: 'Popcorn Ceiling Removal',
    title: 'Popcorn ceiling removal',
    serviceId: 'popcorn',
    intro:
      'Popcorn ceilings scraped, skimmed and retextured in Las Vegas homes for a clean, modern look.',
    coverageIntro: 'We take the ceiling from dated to clean, and protect your home while we work.',
    coverage: [
      { title: 'Prep & protection', text: 'Floors and furniture covered before work starts.' },
      { title: 'Scraping', text: 'The popcorn texture scraped off the ceiling.' },
      { title: 'Skim coat', text: 'A thin coat applied to smooth out the surface.' },
      { title: 'Retexture', text: 'A smooth or light texture applied, your choice.' },
      { title: 'Paint', text: 'Ceilings painted for a finished look.' },
    ],
    sections: [
      {
        title: 'Why remove popcorn ceilings',
        body: [
          'Popcorn texture makes a room feel dated and is hard to clean or repair. Removing it gives your ceilings a cleaner, more modern look.',
        ],
      },
      {
        title: 'What the process looks like',
        body: [
          'We protect the room, scrape off the old texture, skim coat the ceiling and apply the new finish. Then we paint, clean up and leave the room ready to use.',
        ],
      },
    ],
    related: [
      'texture-matching-las-vegas',
      'drywall-services-las-vegas',
      'painting-services-las-vegas',
      'handyman-services-las-vegas',
    ],
  },
  {
    slug: 'texture-matching-las-vegas',
    name: 'Texture Matching',
    title: 'Texture matching',
    serviceId: 'texture',
    intro:
      'Knockdown, orange peel and smooth finishes matched to the original, so repairs in your Las Vegas home blend in with the rest of the wall.',
    coverageIntro: 'A patch only disappears when the texture matches. That’s what this service is for.',
    coverage: [
      { title: 'Knockdown', text: 'The flattened, splatter-style texture common in Las Vegas homes.' },
      { title: 'Orange peel', text: 'A fine, bumpy texture matched in size and spacing.' },
      { title: 'Smooth finish', text: 'A flat, even surface with no visible texture.' },
      { title: 'Patches & repairs', text: 'Texture applied over repaired areas to hide them.' },
      { title: 'Walls & ceilings', text: 'Matching on both vertical and overhead surfaces.' },
    ],
    sections: [
      {
        title: 'Why texture matching matters',
        body: [
          'A drywall patch can be perfectly flat and still stand out if the texture around it is different. Matching the existing texture is what makes a repair blend in.',
        ],
      },
      {
        title: 'How we match it',
        body: [
          'We look at the pattern, size and spacing of your existing texture, then apply and adjust the new texture to fit. After it dries, we paint so the area blends with the rest of the wall.',
        ],
      },
    ],
    related: [
      'drywall-services-las-vegas',
      'popcorn-ceiling-removal-las-vegas',
      'water-damage-repair-las-vegas',
      'painting-services-las-vegas',
    ],
  },
  {
    slug: 'garage-door-services-las-vegas',
    name: 'Garage Door Services',
    title: 'Garage door services',
    serviceId: 'garage-door',
    intro:
      'Garage door repairs, panel replacement and tune-ups for Las Vegas homes to keep your door working smoothly and running quiet.',
    coverageIntro: 'Your garage door gets used every day. We help keep it working the way it should.',
    coverage: [
      { title: 'Repairs', text: 'Doors that stick, shake or don’t close properly.' },
      { title: 'Panel replacement', text: 'Dented or damaged panels swapped out.' },
      { title: 'Tune-ups', text: 'Adjustments to keep the door running smoothly and quietly.' },
      { title: 'Noisy doors', text: 'Squeaks and grinding tracked down and addressed.' },
    ],
    sections: [
      {
        title: 'Small problems, fixed early',
        body: [
          'A noisy or slow garage door is often a sign something needs attention. Fixing it early can help avoid a bigger repair later.',
        ],
      },
      {
        title: 'Panels that look right',
        body: [
          'A dented panel affects how your home looks from the street. Replacing just the damaged panel can make the door look good again without replacing the whole thing.',
        ],
      },
    ],
    related: [
      'handyman-services-las-vegas',
      'stucco-services-las-vegas',
      'painting-services-las-vegas',
      'doors-trim-baseboards-las-vegas',
    ],
  },
  {
    slug: 'doors-trim-baseboards-las-vegas',
    name: 'Doors, Trim & Baseboards',
    title: 'Doors, trim & baseboards',
    serviceId: 'doors-trim',
    intro:
      'Interior doors hung, casing and baseboards installed and caulked in Las Vegas homes — the finishing details that make a room look complete.',
    coverageIntro: 'Trim work is where a room goes from almost done to finished.',
    coverage: [
      { title: 'Interior doors', text: 'New doors hung so they open, close and latch properly.' },
      { title: 'Door casing', text: 'Trim installed around door openings.' },
      { title: 'Baseboards', text: 'Baseboards installed along floors and walls.' },
      { title: 'Caulking', text: 'Gaps sealed for clean lines, ready for paint.' },
      { title: 'Replacement', text: 'Damaged or dated trim swapped for new.' },
    ],
    sections: [
      {
        title: 'Details that finish a room',
        body: [
          'Doors, casing and baseboards cover the gaps where walls meet floors and openings. Installed well, they give a room clean lines and a finished look.',
        ],
      },
      {
        title: 'Great with new floors',
        body: [
          'New flooring often means new baseboards. We can handle both, along with painting, so everything is done together.',
        ],
      },
    ],
    related: [
      'carpentry-services-las-vegas',
      'flooring-installation-las-vegas',
      'painting-services-las-vegas',
      'handyman-services-las-vegas',
    ],
  },
  {
    slug: 'tile-backsplash-las-vegas',
    name: 'Tile & Backsplash',
    title: 'Tile & backsplash',
    serviceId: 'tile',
    intro:
      'Backsplashes, shower surrounds and cracked tile replacement for Las Vegas kitchens and bathrooms.',
    coverageIntro: 'Whether you’re updating a kitchen or fixing a single broken tile, we can help.',
    coverage: [
      { title: 'Kitchen backsplashes', text: 'New tile behind counters, stoves and sinks.' },
      { title: 'Shower surrounds', text: 'Tile installed around showers and tubs.' },
      { title: 'Cracked tile replacement', text: 'Broken tiles removed and replaced.' },
      { title: 'Grout', text: 'Joints grouted for a clean, finished look.' },
    ],
    sections: [
      {
        title: 'An easy kitchen update',
        body: [
          'A new backsplash is one of the simplest ways to change the look of a kitchen without a full remodel.',
        ],
      },
      {
        title: 'Fixing what’s broken',
        body: [
          'A cracked tile can let water get behind it. Replacing damaged tiles keeps your floors and walls looking good and protects what’s underneath.',
        ],
      },
    ],
    related: [
      'flooring-installation-las-vegas',
      'handyman-services-las-vegas',
      'carpentry-services-las-vegas',
      'water-damage-repair-las-vegas',
    ],
  },
]
